import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, PenTool } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { OrdersRemoved } from "../../../../lib/cardOrders";
import {
  downloadLogoOrderFile,
  getLogoOrder,
  getLogoOrders,
  logoOrderFileName,
  removeLogoOrders,
  type LogoOrderFilter,
  type LogoOrderRow as Row,
} from "../../../../lib/logoOrders";
import { Btn, EmptyState, MenuField, SearchField, Toolbar, ToolbarGap, listCard, useToast } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { OrderPickAll, OrderPicksBar, useOrderPicks } from "../orderPicks";
import { LOGO_ORDER_COLS, LogoOrderRow } from "./LogoOrderRow";
import { LogoOrderDialog } from "./LogoOrderDialog";

/** The design's page size, the one the other sales lists use. */
const PAGE_SIZE = 25;

/** The query parameter a link opens one order with (`/sales/logo-orders?order=<id>`). */
const ORDER_PARAM = "order";

/**
 * Sales > Logo files: every order the logo studio on the platform's own sites sent to
 * payment, newest first. Who ordered, which logo, the invoice, and the ZIP of its files to
 * download.
 *
 * Unpaid orders are listed too: somebody who made a logo and reached the payment page is
 * worth knowing about. A logo that never reached it stays in its visitor's browser.
 *
 * Orders can be ticked and deleted: test orders, and unpaid ones that can no longer be paid
 * (the server decides, `orderPicks`). A sale's box is greyed.
 */
export function LogoOrdersList() {
  const t = useT();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<LogoOrderFilter>("all");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Row | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const picks = useOrderPicks(rows);

  const filtered = q !== "" || status !== "all";
  const linked = params.get(ORDER_PARAM);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // A new question starts at the first page: page 3 of a one-page answer looks empty
  useEffect(() => {
    setPage(1);
  }, [q, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLogoOrders({ q: q || undefined, status, page, pageSize: PAGE_SIZE });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      toast(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [q, status, page, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // An order named in the address opens by itself, even when it is not on this page
  useEffect(() => {
    if (!linked || open?.id === linked) return;
    let live = true;
    getLogoOrder(linked)
      .then((order) => live && setOpen(order))
      .catch(() => live && toast(t("sales.logoOrders.loadFailed")));
    return () => {
      live = false;
    };
  }, [linked, open?.id, toast, t]);

  function openOrder(order: Row) {
    setOpen(order);
    setParams(
      (p) => {
        p.set(ORDER_PARAM, order.id);
        return p;
      },
      { replace: true },
    );
  }

  function closeOrder() {
    setOpen(null);
    setParams(
      (p) => {
        p.delete(ORDER_PARAM);
        return p;
      },
      { replace: true },
    );
  }

  async function download(order: Row) {
    if (downloading) return;
    setDownloading(order.id);
    try {
      await downloadLogoOrderFile(order.id, logoOrderFileName(order));
    } catch {
      toast(t("sales.logoOrders.downloadFailed"));
    } finally {
      setDownloading(null);
    }
  }

  function clearFilters() {
    setQInput("");
    setQ("");
    setStatus("all");
  }

  /** After a delete: an open order that is gone closes, and the list loads again (a page emptied by it gives way to the one before). */
  function afterRemove(result: OrdersRemoved) {
    if (open && result.removed.some((r) => r.id === open.id)) closeOrder();
    if (page > 1 && result.removed.length >= rows.length) setPage((p) => p - 1);
    else void load();
  }

  return (
    // One open menu at a time across the filters
    <PopHost>
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("sales.logoOrders.search")} />
        <MenuField
          value={status}
          onChange={setStatus}
          label={t("sales.logoOrders.status")}
          options={[
            { value: "all", label: t("sales.logoOrders.statusAll") },
            { value: "paid", label: t("sales.logoOrders.statusPaid") },
            { value: "pending", label: t("sales.logoOrders.statusPending") },
          ]}
        />
        <ToolbarGap />
        {filtered ? (
          <Btn small onClick={clearFilters}>
            {t("common.clear")}
          </Btn>
        ) : null}
      </Toolbar>

      <OrderPicksBar picks={picks} rows={rows} remove={removeLogoOrders} onRemoved={afterRemove} />

      <div className="crm-card crm-list-stack" style={listCard(LOGO_ORDER_COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PenTool}
            title={t("sales.logoOrders.empty")}
            body={filtered ? t("sales.logoOrders.emptyFiltered") : t("sales.logoOrders.emptyBody")}
            action={filtered ? <Btn onClick={clearFilters}>{t("common.clear")}</Btn> : undefined}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: LOGO_ORDER_COLS, paddingTop: 22 }}>
              <span className="flex min-w-0 items-center gap-3">
                <OrderPickAll picks={picks} rows={rows} />
                <span>{t("sales.col.date")}</span>
              </span>
              <span>{t("sales.logoOrders.order")}</span>
              <span>{t("sales.logoOrders.buyer")}</span>
              <span>{t("sales.logoOrders.invoice")}</span>
              <span className="text-right">{t("sales.logoOrders.amount")}</span>
              <span>{t("sales.logoOrders.status")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((order) => (
                <LogoOrderRow
                  key={order.id}
                  order={order}
                  picks={picks}
                  downloading={downloading === order.id}
                  onOpen={openOrder}
                  onDownload={(o) => void download(o)}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("sales.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + rows.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {open ? (
        <LogoOrderDialog
          order={open}
          downloading={downloading === open.id}
          onDownload={(o) => void download(o)}
          onClose={closeOrder}
        />
      ) : null}
    </PopHost>
  );
}
